import { Head, useForm, Link } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { ArrowLeft, Video } from 'lucide-react'
import { FormEvent } from 'react'

interface Message {
  id: number
  subject: string
  body: string
  video_url: string | null
  status: string
}

interface SenderDefaults {
  email: string
  name: string
}

interface Props extends PageProps {
  message: Message | null
  sender_defaults: SenderDefaults
}

export default function MessageForm({ message, sender_defaults }: Props) {
  const isEdit = !!message

  const { data, setData, post, patch, processing, errors } = useForm({
    subject: message?.subject || '',
    body: message?.body || '',
    video_url: message?.video_url || '',
    status: message?.status || 'draft'
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (isEdit) {
      patch(`/admin/messages/${message.id}`)
    } else {
      post('/admin/messages')
    }
  }

  return (
    <AppLayout>
      <Head title={isEdit ? 'Edit Message' : 'Compose Message'} />

      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Link href="/admin/messages" className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">
            {isEdit ? 'Edit Message' : 'Compose Message'}
          </h1>
        </div>

        <div className="bg-chestnut-cream rounded-lg p-4">
          <p className="text-sm text-chestnut-charcoal/60">Sending from</p>
          <p className="font-medium text-chestnut-charcoal">{sender_defaults.name}</p>
          <p className="text-sm text-chestnut-charcoal/60">{sender_defaults.email}</p>
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mt-3 font-medium">This message will be sent to all active agents when you choose to send it.</p>
        </div>

        <form onSubmit={handleSubmit} className="card space-y-6">
          <div>
            <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Subject *</label>
            <input
              type="text"
              value={data.subject}
              onChange={(e) => setData('subject', e.target.value)}
              className="input-field w-full"
              placeholder="Enter email subject..."
              required
            />
            {errors.subject && <p className="mt-1 text-sm text-status-error">{errors.subject}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Message Body *</label>
            <textarea
              value={data.body}
              onChange={(e) => setData('body', e.target.value)}
              className="input-field w-full min-h-[250px] font-sans"
              placeholder="Write your email message here. HTML formatting is supported."
              required
            />
            {errors.body && <p className="mt-1 text-sm text-status-error">{errors.body}</p>}
            <p className="text-xs text-chestnut-charcoal/50 mt-1">You can use HTML tags for formatting (bold, links, images, etc.)</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">
              <span className="flex items-center gap-2">
                <Video className="h-4 w-4" />
                Video URL (optional)
              </span>
            </label>
            <input
              type="url"
              value={data.video_url}
              onChange={(e) => setData('video_url', e.target.value)}
              className="input-field w-full"
              placeholder="https://www.youtube.com/watch?v=... or paste video link"
            />
            <p className="text-xs text-chestnut-charcoal/50 mt-1">
              If provided, a "Watch Video" button will be included in the email linking to this URL.
            </p>
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t border-chestnut-gray">
            <Link href="/admin/messages" className="btn-secondary">Cancel</Link>
            <button type="submit" disabled={processing} className="btn-primary">
              {processing ? 'Saving...' : (isEdit ? 'Update Message' : 'Save as Draft')}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
